import React, { useMemo, useEffect, useRef } from "react";
import { Layout, Menu, Grid, Tooltip, Space, Divider, Typography, BackTop, Button } from "antd";
import { Link } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import { Iconify } from "utils/Iconify";

// --- Footer menu + social menu from .env with safe defaults ---
const readEnv = (key) => (process.env[key] || "").trim();
const parseJson = (str, fallback) => {
  if (!str) return fallback;
  try {
    const parsed = JSON.parse(str);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch (e) {
    return fallback;
  }
};

// Defaults (icons are Iconify names)
const DEFAULT_FOOTER_MENU_CONFIG = [
  { key: "IdVerification", label: "Id Verification", path: "#", icon: "ph:identification-card-light" },
  { key: "ProofOfLife", label: "Proof of Life", path: "#", icon: "mdi:heart-pulse" },
  { key: "MaritalStatus", label: "Marital Status", path: "#", icon: "ph:users-three-light" },
  { key: "EmploymentDetails", label: "Employment Details", path: "#", icon: "ph:briefcase-light" },
  { key: "ContactDetailsVerification", label: "Contact Verification", path: "#", icon: "ph:address-book-light" },
  { key: "BusinessVerification", label: "Business Verification", path: "#", icon: "ph:buildings-light" },
];


const DEFAULT_SOCIAL_MENU_CONFIG = [
  { key: "LinkedIn", title: "LinkedIn", href: "#", icon: "mdi:linkedin" },
  { key: "GitHub", title: "GitHub", href: "#", icon: "mdi:github" },
  { key: "X", title: "X", href: "#", icon: "mdi:twitter" },
  { key: "YouTube", title: "YouTube", href: "#", icon: "mdi:youtube" },
];


const FALLBACK_ICONS = {
  IdVerification: "ph:identification-card-light",
  ProofOfLife: "mdi:heart-pulse",
  MaritalStatus: "ph:users-three-light",
  EmploymentDetails: "ph:briefcase-light",
  ContactDetailsVerification: "ph:address-book-light",
  BusinessVerification: "ph:buildings-light",
};

const SHORT_LABELS = {
  "Contact Details Verification": "Contact Verification",
};

// Builders from data-only config -> AntD menu items
const getLabel = (text, path, icon) => {
  const commonStyle = { display: 'inline-flex', gap: 8, alignItems: 'center' };
  if (!path || path === '#') {
    return (
      <span aria-label={text} title={text} style={{ ...commonStyle, cursor: 'default' }}>
        {icon}
        <span>{text}</span>
      </span>
    );
  }
  return (
    <Link to={path} aria-label={text} title={text} style={commonStyle}>
      {icon}
      <span>{text}</span>
    </Link>
  );
};

const buildFooterItems = (cfg) =>
  (cfg || []).map((item) => {
    const safeKey = item.key || item.label || "Item";
    const compactLabelKey = (item.label || "").replace(/\s+/g, "");
    const iconName =
      item.icon ||
      FALLBACK_ICONS[safeKey] ||
      FALLBACK_ICONS[compactLabelKey] ||
      "ph:squares-four-light";
    const displayText = SHORT_LABELS[item.label] || item.label || safeKey;
    return {
      key: safeKey,
      label: getLabel(
        displayText,
        item.path || "#",
        <Iconify icon={iconName} width={16} color="currentColor" />
      ),
    };
  });

const buildSocialItems = (cfg) =>
  (cfg || []).map((item) => ({
    key: item.key || item.title,
    label: (
      <a
        href={item.href || "#"}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={item.title || item.key}
        title={item.title || item.key}
        style={{ display: 'inline-flex', alignItems: 'center' }}
      >
        <Iconify icon={item.icon || "mdi:share-variant"} width={18} />
      </a>
    ),
  }));

const FOOTER_MENU_DATA = parseJson(readEnv("REACT_APP_FOOTER_MENU"), DEFAULT_FOOTER_MENU_CONFIG);
const SOCIAL_MENU_DATA = parseJson(readEnv("REACT_APP_SOCIAL_MENU"), DEFAULT_SOCIAL_MENU_CONFIG);

const footerMenu = buildFooterItems(FOOTER_MENU_DATA);
const socialMenu = buildSocialItems(SOCIAL_MENU_DATA);
// --- end env-driven menus ---

const { Footer } = Layout;
const { Text } = Typography;

const AntdFooter = () => {
  const { user, logout } = useAuth();
  const username = user?.username || '';
  const rawVersion = (process.env.REACT_APP_VERSION || "").trim();
  const versionMatch = rawVersion.match(/\d+(?:\.\d+)+/); // pick first numeric x.y(.z) sequence
  const appVersion = (versionMatch ? versionMatch[0] : "2.0001");
  const initial = (username || 'U').charAt(0).toUpperCase();
  const handleLogout = (e) => {
    e.preventDefault();
    try { logout(); } catch (_) {}
    const redir = process.env.REACT_APP_LOGOUT_REDIRECT || "/";
    window.location.href = redir;
  };

  const screens = Grid.useBreakpoint();
  const menuMode = screens.md ? "horizontal" : "inline";

  // Wrap social labels with tooltips for nicer affordance
  const socialItems = useMemo(() => {
    return (socialMenu || []).map((item) => {
      const title =
        (typeof item?.label === "string" && item.label) ||
        (item?.label?.props?.children) ||
        item?.key ||
        "Social";
      return {
        ...item,
        label: <Tooltip placement="top" title={title}>{item.label}</Tooltip>,
      };
    });
  }, []);

  const items = footerMenu || [];
  const ruleMargin = screens.md ? "8px 0 8px" : "4px 0 8px";
  const copyRef = useRef(null);
  useEffect(() => {
    const el = copyRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      el.classList.add('in-view');
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          el.classList.add('in-view');
          obs.unobserve(el);
        }
      });
    }, { threshold: 0.2 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const el = copyRef.current;
    if (!el) return;

    try {
      const storageKey = 'appverseFooterTyped_v1';
      const prefersReduced = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if ((typeof localStorage !== 'undefined' && localStorage.getItem(storageKey)) || prefersReduced) return;

      const start = () => {
        const textLen = (el.textContent || '').trim().length;
        // Measure natural width of the content for the typewriter animation
        const prevWS = el.style.whiteSpace;
        const prevW = el.style.width;
        el.style.whiteSpace = 'nowrap';
        el.style.width = 'auto';
        const w = el.scrollWidth;
        el.style.whiteSpace = prevWS;
        el.style.width = prevW;

        const steps = Math.max(16, Math.min(60, textLen));
        el.style.setProperty('--typeWidth', w + 'px');
        el.style.setProperty('--typeSteps', String(steps));

        // Disable shimmer during typing, then enable again after
        el.classList.add('typing', 'typewriter');

        const onEnd = (e) => {
          if (e.animationName !== 'typing') return;
          el.classList.remove('typing', 'typewriter');
          el.classList.add('typed-once');
          el.style.removeProperty('--typeWidth');
          el.style.removeProperty('--typeSteps');
          try { localStorage.setItem(storageKey, '1'); } catch (_) {}
          el.removeEventListener('animationend', onEnd);
        };
        el.addEventListener('animationend', onEnd);
      };

      if (el.classList.contains('in-view')) {
        start();
      } else if (typeof MutationObserver !== 'undefined') {
        const mo = new MutationObserver(() => {
          if (el.classList.contains('in-view')) {
            start();
            mo.disconnect();
          }
        });
        mo.observe(el, { attributes: true, attributeFilter: ['class'] });
        return () => mo.disconnect();
      }
    } catch (_) {
      // no-op on environments without required APIs
    }
  }, []);

  const currentYear = new Date().getFullYear();
  const defaultCopyright = `© ${currentYear} iSekurex Capital Developed by Engineers On Call`;
  const copyrightText = process.env.REACT_APP_COPYRIGHT_TEXT || defaultCopyright;

  return (
    <Footer role="contentinfo" className="appverse-footer" style={{ paddingInline: screens.md ? 48 : 16, paddingBlock: 8 }}>
      <div className="footer-gradient-rule" style={{ margin: ruleMargin }} aria-hidden />

      <Space
        direction={screens.md ? "horizontal" : "vertical"}
        size={screens.md ? 24 : 12}
        style={{ width: "100%", justifyContent: "space-between", display: "flex", alignItems: "center" }}
      >
        <nav aria-label="Footer" style={{ flex: 1, width: "100%" }}>
          <div className="footer-inline-menu" role="list">
            {items.map((it, idx) => (
              <div key={it.key} role="listitem" className={`footer-inline-item accent-${idx % 6}`}>{it.label}</div>
            ))}
          </div>
        </nav>

        <Space size={screens.md ? 16 : 8} align="center" wrap>
          <nav aria-label="Social">
            <Menu
              mode="horizontal"
              className="menu-nav-social"
              items={socialItems}
              style={{ background: "transparent" }}
            />
          </nav>
          <Button
            className="footer-logout"
            onClick={handleLogout}
            aria-label={`Log out ${username || 'current user'}`}
            title={username ? `Log out ${username}` : 'Log out'}
          >
            <span className="logout-avatar" aria-hidden="true">{initial}</span>
            <span className="logout-text">
              Log out{username ? ' ' : ''}{username ? <strong className="username">@{username}</strong> : null}
            </span>
            <span className="logout-icon" aria-hidden="true">
              <Iconify icon="mdi:logout" width={16} />
            </span>
          </Button>
        </Space>
      </Space>

      <Space direction="vertical" size={8} style={{ width: "100%", marginTop: 4, textAlign: screens.md ? "left" : "center" }}>
        <Text className="copyright" role="note">
          <span
            ref={copyRef}
            className="copyright-line"
            dangerouslySetInnerHTML={{ __html: copyrightText }}
          />
          <span className="version-chip" aria-label={`Version ${appVersion}`}>
            <strong>v{appVersion}</strong>
          </span>
        </Text>
      </Space>
      <style>{`
        .appverse-footer {
          /* Layered gradients for depth */
          background:
            radial-gradient(1200px 300px at 10% -10%, rgba(99,102,241,0.25), transparent 60%),
            radial-gradient(1200px 300px at 90% -10%, rgba(56,189,248,0.25), transparent 60%),
            linear-gradient(180deg, #0b1220 0%, hsl(223 24.21% 6.47%) 100%);
          color: rgba(255,255,255,0.88);
          position: relative;
          /* Chip variables for footer menu chips */
          --chip-fg: #ffffff;
          --chip-border: rgba(99,102,241,0.55);
          --chip-bg: linear-gradient(135deg, rgba(99,102,241,0.22), rgba(56,189,248,0.14));
          --chip-hover-border: rgba(99,102,241,0.9);
          --chip-shadow: rgba(99,102,241,0.28);
          --chip-glow1: rgba(99,102,241,0.22);
          --chip-glow2: rgba(56,189,248,0.18);
          background-color: #0b1220; /* fallback to ensure contrast on all themes */
          isolation: isolate;
          overflow: hidden;
          /* Consistent sizing variables */
          --chip-height: 40px;
          --chip-pad-x: 14px;
          --chip-font: 14px;
          --chip-radius: 14px;
          --chip-icon: 16px;
          --social-size: 36px;
          --chip-width: 220px;
        }
        .appverse-footer .ant-menu,
        .appverse-footer .ant-typography { background: transparent; color: inherit; }

        .footer-gradient-rule {
          height: 1px;
          width: 100%;
          background: linear-gradient(90deg, transparent, rgba(99,102,241,0.6), rgba(56,189,248,0.6), transparent);
          position: relative;
          overflow: hidden;
        }

        .footer-gradient-rule::after {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent);
          transform: translateX(-100%);
          animation: sweep 4s ease-in-out infinite;
        }
        @keyframes sweep {
          0%, 20% { transform: translateX(-100%); }
          50% { transform: translateX(100%); }
          100% { transform: translateX(100%); }
        }

        .appverse-footer .menu-nav-social .ant-menu-item a,
        .appverse-footer .menu-nav-social .ant-menu-title-content {
          background: linear-gradient(90deg, #6366f1, #38bdf8);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          background-size: 200% 100%;
          transition: background-position 200ms ease;
        }
        .appverse-footer .menu-nav-social .ant-menu-item:hover .ant-menu-title-content,
        .appverse-footer .menu-nav-social .ant-menu-item:hover a {
          filter: brightness(1.15);
        }
        .appverse-footer .menu-nav-social .ant-menu-item:hover a,
        .appverse-footer .menu-nav-social .ant-menu-item:hover .ant-menu-title-content {
          background-position: 100% 0;
        }

        .appverse-footer .copyright {
          color: rgba(255,255,255,0.75);
        }
        .appverse-footer .copyright-line {
          display: inline-block;
          background: linear-gradient(90deg, rgba(255,255,255,0.78), rgba(99,102,241,0.95), rgba(56,189,248,0.95), rgba(255,255,255,0.78));
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          background-size: 200% 100%;
          background-position: 0% 50%;
          animation: copyrightShimmer 8s ease-in-out infinite;
          opacity: 0;
          transform: translateY(8px);
          filter: blur(6px);
          transition: opacity 360ms ease, transform 360ms ease, filter 360ms ease, background-position 200ms ease;
        }
        .appverse-footer .copyright-line.in-view {
          opacity: 1;
          transform: translateY(0);
          filter: blur(0);
        }
        .appverse-footer .copyright-line:hover {
          background-position: 100% 50%;
        }
        @keyframes copyrightShimmer {
          0% { background-position: 0% 50%; }
          60% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        /* Link treatment inside the copyright line for accessibility */
        .appverse-footer .copyright-line a {
          color: #e6f2ff;
          text-decoration: underline;
          text-underline-offset: 2px;
          transition: text-shadow 140ms ease, transform 140ms ease;
        }
        .appverse-footer .copyright-line a:hover {
          text-shadow: 0 2px 10px rgba(56,189,248,0.35);
          transform: translateY(-1px);
        }

        /* --- Copyright typewriter (first view only) --- */
        .appverse-footer .copyright-line.typing {
          /* Temporarily disable shimmer while typing */
          animation: none;
        }
        .appverse-footer .copyright-line.typewriter {
          position: relative;
          white-space: nowrap;
          overflow: hidden;
          width: 0; /* animated to --typeWidth */
          border-right: 2px solid rgba(255,255,255,0.85); /* caret */
          animation: typing var(--typeDur, 2200ms) steps(var(--typeSteps, 32)) 1 both,
                     blinkCaret 900ms steps(1, end) infinite;
        }
        .appverse-footer .copyright-line.typed-once {
          border-right-color: transparent;
        }
        @keyframes typing {
          from { width: 0; }
          to { width: var(--typeWidth, 100%); }
        }
        @keyframes blinkCaret {
          0%, 49% { border-color: rgba(255,255,255,0.85); }
          50%, 100% { border-color: transparent; }
        }

        .appverse-footer .backtop-grad.ant-btn {
          border: none;
          background: linear-gradient(90deg, #6366f1, #38bdf8);
          color: #fff;
          box-shadow: 0 6px 20px rgba(56,189,248,0.35);
          transition: transform 120ms ease, box-shadow 120ms ease;
          background-size: 200% 100%;
          animation: buttonShimmer 6s linear infinite;
        }
        .appverse-footer .backtop-grad.ant-btn:hover {
          box-shadow: 0 8px 24px rgba(56,189,248,0.5);
          transform: translateY(-1px);
        }
        @keyframes buttonShimmer {
          0% { background-position: 0% 50%; }
          100% { background-position: 100% 50%; }
        }

        @media (max-width: 767px) {
          .appverse-footer {
            --chip-height: 38px;
            --chip-pad-x: 12px;
            --chip-font: 13px;
            --chip-radius: 12px;
            --chip-icon: 16px;
            --social-size: 32px;
            --chip-width: 180px;
          }
        }

        .appverse-footer .ant-menu-horizontal > .ant-menu-item,
        .appverse-footer .ant-menu-inline .ant-menu-item {
          transition: transform 140ms ease, text-shadow 140ms ease;
          will-change: transform;
        }
        .appverse-footer .ant-menu-item:hover {
          transform: translateY(-2px);
          text-shadow: 0 2px 10px rgba(56,189,248,0.2);
        }

        /* --- Inline footer menu: always visible, wraps instead of collapsing --- */
        .appverse-footer .footer-inline-menu {
          display: flex;
          flex-wrap: wrap;
          align-items: stretch;
          gap: 12px 16px; /* row-gap column-gap */
          position: relative;
          z-index: 1;
          justify-content: flex-start;
        }
        .appverse-footer .footer-inline-item {
          display: inline-flex;
          align-items: center;
          color: rgba(255,255,255,0.88) !important;
          transition: transform 140ms ease, text-shadow 140ms ease;
          flex: 0 0 var(--chip-width);
          max-width: var(--chip-width);
        }
        .appverse-footer .footer-inline-item:hover {
          transform: translateY(-2px);
          text-shadow: 0 2px 10px rgba(56,189,248,0.2);
        }
        .appverse-footer .footer-inline-item :where(a,span) {
          display: inline-flex;
          gap: 8px;
          align-items: center;
          height: var(--chip-height);
          padding: 0 var(--chip-pad-x);
          border-radius: var(--chip-radius);
          font-size: var(--chip-font);
          background: var(--chip-bg);
          border: 1px solid var(--chip-border);
          box-shadow: 0 8px 28px rgba(2,6,23,0.22), inset 0 1px 0 rgba(255,255,255,0.10);
          color: var(--chip-fg) !important;
          text-decoration: none;
          line-height: 1;
          white-space: nowrap;
          position: relative;
          overflow: hidden;
          font-weight: 600;
          width: 100%;
          justify-content: center;
          background-size: 200% 200%;
          transition: transform 140ms ease, box-shadow 180ms ease, border-color 180ms ease, background-position 260ms ease;
        }
        .appverse-footer .footer-inline-item :where(a,span)::after {
          content: "";
          position: absolute;
          inset: 0;
          background:
            radial-gradient(120% 200% at -10% 10%, var(--chip-glow1), transparent 55%),
            radial-gradient(120% 200% at 110% -10%, var(--chip-glow2), transparent 55%);
          opacity: 0.9;
          pointer-events: none;
        }
        /* --- Tiny glossy highlight sweep on hover --- */
        .appverse-footer .footer-inline-item :where(a,span)::before {
          content: "";
          position: absolute;
          top: -30%;
          bottom: -30%;
          left: -40%;
          width: 36%;
          pointer-events: none;
          background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.7) 50%, rgba(255,255,255,0) 100%);
          filter: blur(10px);
          opacity: 0;
          transform: translateX(-120%) rotate(18deg);
          transition: transform 600ms ease, opacity 300ms ease;
          z-index: 1;
        }
        .appverse-footer .footer-inline-item :where(a,span):hover::before {
          opacity: 0.85;
          transform: translateX(260%) rotate(18deg);
        }
        .appverse-footer .footer-inline-item :where(a,span):hover {
          transform: translateY(-2px);
          border-color: var(--chip-hover-border);
          box-shadow: 0 10px 28px var(--chip-shadow);
          background-position: 100% 0;
        }
        .appverse-footer .footer-inline-item :where(a,span):focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(56,189,248,0.45);
          border-color: rgba(56,189,248,0.9);
        }
        .appverse-footer .footer-inline-item svg {
          color: inherit;
          fill: currentColor;
          opacity: 0.95;
          width: var(--chip-icon);
          height: var(--chip-icon);
          flex: 0 0 var(--chip-icon);
        }
        .appverse-footer .menu-nav-social .ant-menu-item a {
          width: var(--social-size);
          height: var(--social-size);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
        }
        .appverse-footer .menu-nav-social .ant-menu-item a svg,
        .appverse-footer .menu-nav-social .ant-menu-item .ant-menu-title-content svg {
          width: calc(var(--social-size) * 0.55) !important;
          height: calc(var(--social-size) * 0.55) !important;
        }
        /* --- Accent variants for chips --- */
        .appverse-footer .footer-inline-item.accent-0 { /* Blue */
          --chip-fg: #ffffff;
          --chip-border: rgba(96,165,250,0.95);
          --chip-bg: linear-gradient(135deg, rgba(59,130,246,0.92), hsl(221 86.19% 53.33% / 0.92));
          --chip-hover-border: rgba(147,197,253,1);
          --chip-shadow: rgba(59,130,246,0.38);
          --chip-glow1: rgba(59,130,246,0.35);
          --chip-glow2: rgba(37,99,235,0.28);
        }
        .appverse-footer .footer-inline-item.accent-1 { /* Cyan */
          --chip-fg: #ffffff;
          --chip-border: rgba(34,211,238,0.95);
          --chip-bg: linear-gradient(135deg, rgba(6,182,212,0.92), hsl(192 94.40% 36.47% / 0.92));
          --chip-hover-border: rgba(125,211,252,1);
          --chip-shadow: rgba(6,182,212,0.38);
          --chip-glow1: rgba(6,182,212,0.35);
          --chip-glow2: rgba(8,145,178,0.28);
        }
        .appverse-footer .footer-inline-item.accent-2 { /* Violet */
          --chip-fg: #ffffff;
          --chip-border: rgba(167,139,250,0.95);
          --chip-bg: linear-gradient(135deg, rgba(139,92,246,0.92), hsl(262 86.26% 57.84% / 0.92));
          --chip-hover-border: rgba(196,181,253,1);
          --chip-shadow: rgba(139,92,246,0.38);
          --chip-glow1: rgba(139,92,246,0.35);
          --chip-glow2: rgba(124,58,237,0.28);
        }
        .appverse-footer .footer-inline-item.accent-3 { /* Emerald */
          --chip-fg: #ffffff;
          --chip-border: rgba(52,211,153,0.95);
          --chip-bg: linear-gradient(135deg, rgba(16,185,129,0.92), hsl(161 96.55% 30.39% / 0.92));
          --chip-hover-border: rgba(110,231,183,1);
          --chip-shadow: rgba(16,185,129,0.38);
          --chip-glow1: rgba(16,185,129,0.35);
          --chip-glow2: rgba(5,150,105,0.28);
        }
        .appverse-footer .footer-inline-item.accent-4 { /* Amber */
          --chip-fg: #1b1110;
          --chip-border: rgba(251,191,36,0.95);
          --chip-bg: linear-gradient(135deg, rgba(245,158,11,0.95), hsl(32 97.62% 43.73% / 0.95));
          --chip-hover-border: rgba(253,230,138,1);
          --chip-shadow: rgba(245,158,11,0.38);
          --chip-glow1: rgba(245,158,11,0.45);
          --chip-glow2: rgba(217,119,6,0.35);
        }
        .appverse-footer .footer-inline-item.accent-5 { /* Rose */
          --chip-fg: #ffffff;
          --chip-border: rgba(251,113,133,0.95);
          --chip-bg: linear-gradient(135deg, rgba(244,63,94,0.92), hsl(347 80.17% 49.80% / 0.92));
          --chip-hover-border: rgba(254,205,211,1);
          --chip-shadow: rgba(244,63,94,0.38);
          --chip-glow1: rgba(244,63,94,0.35);
          --chip-glow2: rgba(225,29,72,0.28);
        }

        .appverse-footer::before {
          content: "";
          position: absolute;
          inset: -10% -5% auto -5%;
          height: 50%;
          pointer-events: none;
          background: radial-gradient(50% 60% at 50% 10%, rgba(56,189,248,0.08), transparent 70%);
          animation: footerFloat 24s ease-in-out infinite alternate;
        }
        @keyframes footerFloat {
          0% { transform: translate3d(-1%, -1%, 0) scale(1); }
          50% { transform: translate3d(1%, 0.5%, 0) scale(1.02); }
          100% { transform: translate3d(0.5%, -0.5%, 0) scale(1.01); }
        }

        @media (prefers-reduced-motion: reduce) {
          .appverse-footer *,
          .appverse-footer *::before,
          .appverse-footer *::after {
            animation: none !important;
            transition: none !important;
          }
        }

        /* Footer Logout pill — luxe gold */
        .appverse-footer .footer-logout{
          position: relative;
          display:inline-flex; align-items:center; gap:8px; padding:8px 14px; border-radius:999px;
          background: linear-gradient(180deg, rgba(253,224,71,0.98), rgba(234,179,8,0.96));
          border:1px solid rgba(251,191,36,0.65);
          color:#0b0b12;
          font-weight:700;
          height: auto;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.35), 0 10px 22px rgba(251,191,36,0.22);
          overflow: hidden;
        }
        .appverse-footer .footer-logout::before{
          content:""; position:absolute; top:-40%; bottom:-40%; left:-30%; width:36%;
          background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.75) 50%, rgba(255,255,255,0) 100%);
          filter: blur(10px); opacity: 0; transform: translateX(-120%) rotate(18deg);
          transition: transform 700ms ease, opacity 360ms ease;
          pointer-events:none;
        }
        .appverse-footer .footer-logout:hover{ filter:saturate(1.06) brightness(1.04); transform: translateY(-1px); box-shadow: 0 12px 26px rgba(251,191,36,0.28); }
        .appverse-footer .footer-logout:hover::before{ opacity:.9; transform: translateX(260%) rotate(18deg); }
        .appverse-footer .footer-logout .logout-avatar{
          width: 22px; height: 22px; border-radius: 999px; display:inline-flex; align-items:center; justify-content:center;
          background:#fde68a; color:#111827; font-size:12px; font-weight:800; box-shadow: inset 0 1px 0 rgba(255,255,255,0.45);
        }
        .appverse-footer .footer-logout .logout-text{ line-height:1; display:inline-flex; gap:4px; align-items:center; }
        .appverse-footer .footer-logout .username{ color:#0b0b12; font-weight:800; }
        .appverse-footer .footer-logout .logout-icon{ display:inline-flex; align-items:center; opacity:.9; }
        /* Version chip — stronger contrast + isolation */
        .appverse-footer .version-chip{
          position: relative;
          display:inline-flex; align-items:center; gap:6px; margin-left:10px; padding:4px 10px; border-radius:999px;
          background: linear-gradient(180deg, rgba(253,224,71,0.42), rgba(253,224,71,0.22));
          border:1px solid rgba(251,191,36,0.75);
          color:#0b0b12; font-size:12px; font-weight:800; line-height:1;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.25), 0 6px 16px rgba(251,191,36,0.20);
          z-index: 2; isolation: isolate; white-space: nowrap;
        }
        .appverse-footer .version-chip strong{ color:#0b0b12; font-weight:800; letter-spacing:.2px; }
        @media (max-width: 767px){
          .appverse-footer .version-chip{ margin-left:8px; font-size:11px; padding:3px 9px; }
        }
      `}</style>
    </Footer>
  );
};

export default AntdFooter;